import { ChevronDownIcon } from '@radix-ui/react-icons'
import * as Select from '@radix-ui/react-select'
import clsx from 'clsx'
import { CheckIcon } from 'lucide-react'
import React, { useState } from 'react'
import { TailSpin } from 'react-loading-icons'

interface DropdownProps<T, S> {
  value: string;
  outputValue: string;
  handleSetValue: React.Dispatch<React.SetStateAction<S>> | ((v: S) => void);
  options: [string, T][];
  placeholder: string;
  optionsMapCallback: (tuple: [string, T]) => React.JSX.Element;
  label: string;
  LoadingIndicator?: (props: React.JSX.IntrinsicAttributes & React.SVGProps<SVGSVGElement>) => React.ReactElement;
  isLoading?: boolean;
  isDisabled?: boolean;
}

function Dropdown<T, S> ({
  value,
  outputValue,
  handleSetValue,
  options,
  placeholder,
  optionsMapCallback,
  label,
  LoadingIndicator = TailSpin,
  isLoading,
  isDisabled,
}: DropdownProps<T, S>) {
  return (
    <div>
      <Select.Root value={value} onValueChange={(v) => handleSetValue(v as S)} disabled={isDisabled}>
        <div className='text-sm italic py-2'>{label}</div>
        <Select.Trigger
          className='w-full max-w-56 text-gray-500 flex items-center justify-between rounded py-2 px-4 bg-white focus:outline-none cursor-pointer shadow ring-1 ring-black ring-opacity-5 md:rounded-lg'
          aria-label='Period Options'
        >
          <Select.Value placeholder={placeholder}>
            {isLoading
              ? <LoadingIndicator
                  stroke='#4f46e5'
                  fill='#4f46e5'
                  style={{ width: '40px', height: '24px' }}
                />
              : outputValue}
          </Select.Value>

          <Select.Icon className='pl-2'>
            <ChevronDownIcon />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position='popper'
            sideOffset={5}
            className='w-56 overflow-hidden rounded-md border border-gray-200'
          >
            <Select.ScrollUpButton />

            <Select.Viewport>{options.map(optionsMapCallback)}</Select.Viewport>

            <Select.ScrollDownButton />
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  )
}

export const SelectItem = React.forwardRef<
  HTMLDivElement,
  {
    value: string;
    outputValue: string;
    currentValue: string;
    isDisabled?: boolean;
  }
>(({ value, outputValue, currentValue, isDisabled }, forwardedRef) => {
  const [isFocused, setIsFocused] = useState(false)

  const isCurrentlySelected = value === currentValue
  return (
    <Select.Item
      className={clsx(
        'p-2 px-4 text-gray-500 flex items-center justify-between relative focus:outline-none cursor-pointer align-center content-center',
        { 'text-thatch-purple-secondary': isCurrentlySelected },
        { 'bg-thatch-purple-secondary text-white': isFocused },
        { 'bg-white': !isFocused }
      )}
      value={value}
      ref={forwardedRef}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      disabled={isDisabled}
    >
      <Select.ItemText>{outputValue}</Select.ItemText>
      <Select.ItemIndicator className='pl-2'>
        <CheckIcon />
      </Select.ItemIndicator>
    </Select.Item>
  )
})
SelectItem.displayName = 'SelectItem'

export default Dropdown
